(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const WALLET_TIMEOUT = 12000;
  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

  const moneyINR = (rupees) => {
    try { return new Intl.NumberFormat('en-IN', { style:'currency', currency:'INR', maximumFractionDigits: 0 }).format(Number(rupees) || 0); }
    catch (_) { return `₹${Math.round(Number(rupees) || 0)}`; }
  };
  const fmtDC = (amount) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.floor(Number(amount) || 0));
  const storage = {
    get(k) { try { return localStorage.getItem(k) || ''; } catch (_) { return ''; } },
    set(k,v) { try { localStorage.setItem(k, String(v)); } catch (_) {} },
    del(k) { try { localStorage.removeItem(k); } catch (_) {} }
  };

  async function fetchWithTimeout(url, options = {}, timeout = WALLET_TIMEOUT) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try { return await fetch(url, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timer); }
  }

  async function getCart() {
    const r = await fetch(`${root}cart.js`, { headers:{ Accept:'application/json' }, cache:'no-store' });
    if (!r.ok) throw new Error('Could not read cart.');
    return r.json();
  }

  async function setCartDiscounts(codes) {
    const unique = [...new Set(codes.map(v => String(v || '').trim()).filter(Boolean))];
    const r = await fetch(`${root}cart/update.js`, {
      method:'POST',
      headers:{ 'Content-Type':'application/json', Accept:'application/json' },
      body:JSON.stringify({ discount: unique.join(',') })
    });
    if (!r.ok) throw new Error('Could not update cart discounts.');
    return r.json();
  }

  const couponCode = () => storage.get('dili-selected-coupon');
  const creditCode = () => storage.get('dili-credit-code');

  async function backendRemoveCredit(wallet) {
    const endpoint = wallet?.dataset.creditRemoveEndpoint;
    if (!endpoint) return;
    try {
      await fetchWithTimeout(endpoint, {
        method:'POST', credentials:'include', mode:'cors', cache:'no-store',
        headers:{ Accept:'application/json', 'Content-Type':'application/json' },
        body:'{}'
      });
    } catch (_) {}
    storage.del('dili-credit-code');
    storage.del('dili-credit-amount');
  }

  async function removeCredits(wallet, { reload = true } = {}) {
    const status = wallet?.querySelector('[data-dili-credit-status]');
    if (status) status.textContent = 'Removing DILI Credits…';
    const coupon = couponCode();
    try { await setCartDiscounts(coupon ? [coupon] : []); } catch (_) {}
    await backendRemoveCredit(wallet);
    if (reload) window.location.reload();
  }

  async function applyCoupon(raw) {
    const code = String(raw || '').trim().toUpperCase();
    const status = document.querySelector('[data-dili-safe-code-status]');
    if (!code) { if (status) status.textContent = 'Enter a coupon code.'; return; }
    const wallet = document.querySelector('[data-dili-wallet]');
    if (status) status.textContent = `Applying ${code}…`;
    try {
      // Coupon first, credits second. This guarantees the selected DC amount is
      // calculated from the final post-coupon cart value.
      if (creditCode()) await removeCredits(wallet, { reload:false });
      storage.set('dili-selected-coupon', code);
      await setCartDiscounts([code]);
      window.location.reload();
    } catch (e) {
      if (status) status.textContent = e.message || 'Could not apply this coupon.';
    }
  }

  async function loadWallet(wallet) {
    if (!wallet) return;
    const loading = wallet.querySelector('[data-dili-wallet-loading]');
    const ready = wallet.querySelector('[data-dili-wallet-ready]');
    const guest = wallet.querySelector('[data-dili-wallet-guest]');
    const errorBox = wallet.querySelector('[data-dili-wallet-error]');
    const balanceEl = wallet.querySelector('[data-dili-credit-balance]');
    const input = wallet.querySelector('[data-dili-credit-input]');
    const appliedBox = wallet.querySelector('[data-dili-credit-applied]');
    const appliedAmount = wallet.querySelector('[data-dili-credit-applied-amount]');
    const spendBox = wallet.querySelector('[data-dili-credit-spend]');
    const payable = wallet.querySelector('[data-dili-cart-payable]');
    const checkout = wallet.querySelector('[data-dili-credit-checkout]');
    const accountLink = wallet.querySelector('.dili-wallet-account-link');
    const endpoint = wallet.dataset.creditEndpoint;
    const show = (n) => { if (n) n.hidden = false; };
    const hide = (n) => { if (n) n.hidden = true; };
    hide(ready); hide(guest); hide(errorBox); show(loading);

    try {
      const r = await fetchWithTimeout(endpoint, { method:'GET', credentials:'include', mode:'cors', cache:'no-store', headers:{ Accept:'application/json' } });
      const data = await r.json().catch(() => ({}));
      hide(loading);
      if (r.status === 401 || data.logged_in === false) { show(guest); return; }
      if (!r.ok || !data.ok) throw new Error(data.message || 'Wallet unavailable');

      const balance = Math.floor(Number(data.credit?.dc ?? data.credit?.balance ?? 0));
      const cart = await getCart();
      const cartRupees = Math.max(0, Math.floor(Number(cart.total_price || 0) / 100));
      if (balanceEl) balanceEl.textContent = fmtDC(balance);
      if (accountLink && data.account_url) accountLink.href = data.account_url;
      if (input) {
        input.max = String(Math.max(0, Math.min(balance, cartRupees)));
        input.placeholder = balance > 0 ? `Up to ${fmtDC(Math.min(balance, cartRupees))}` : '0';
        if (!input.value && balance > 0) input.value = String(Math.min(balance, cartRupees));
        input.disabled = balance < 1 || cartRupees < 1;
      }
      if (payable) payable.textContent = moneyINR(cartRupees);
      if (checkout) checkout.textContent = cartRupees > 0 ? `CHECKOUT • ${moneyINR(cartRupees)}` : 'COMPLETE ORDER';

      const reservation = data.reservation;
      const storedCode = creditCode();
      const reservedAmount = Math.floor(Number(reservation?.amount || 0));
      if (reservation && reservedAmount > 0 && storedCode && String(reservation.code || '').toUpperCase() === storedCode.toUpperCase()) {
        if (appliedAmount) appliedAmount.textContent = fmtDC(reservedAmount);
        hide(spendBox); show(appliedBox);
      } else {
        if (!reservation) { storage.del('dili-credit-code'); storage.del('dili-credit-amount'); }
        show(spendBox); hide(appliedBox);
      }
      show(ready);
    } catch (_) {
      hide(loading); show(errorBox);
    }
  }

  async function applyCredits(wallet) {
    const status = wallet.querySelector('[data-dili-credit-status]');
    const input = wallet.querySelector('[data-dili-credit-input]');
    const button = wallet.querySelector('[data-dili-credit-apply]');
    const amount = Math.floor(Number(input?.value || 0));
    if (amount < 1) { if (status) status.textContent = 'Enter how many DILI Credits you want to use.'; return; }
    if (status) status.textContent = `Applying ${fmtDC(amount)} DC…`;
    if (button) button.disabled = true;
    try {
      const before = await getCart();
      const cartTotal = Number(before.total_price || 0) / 100;
      const endpoint = wallet.dataset.creditApplyEndpoint;
      const r = await fetchWithTimeout(endpoint, {
        method:'POST', credentials:'include', mode:'cors', cache:'no-store',
        headers:{ 'Content-Type':'application/json', Accept:'application/json' },
        body:JSON.stringify({ amount, cart_total:cartTotal, cart_token:String(before.token || '') })
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || data.logged_in === false) { window.location.href = data.account_url || 'https://my.diliclothing.com/credits'; return; }
      if (!r.ok || !data.ok || !data.code) {
        const msg = String(data.message || 'Could not apply DILI Credits.');
        if (data.code === 'missing_write_discounts' || /write_discounts/i.test(msg)) {
          throw new Error('DILI Credits are not activated for cart redemption yet.');
        }
        throw new Error(msg);
      }

      const coupon = couponCode();
      const after = await setCartDiscounts([coupon, data.code].filter(Boolean));
      const beforeTotal = Number(before.total_price || 0);
      const afterTotal = Number(after.total_price || 0);
      if (!(afterTotal < beforeTotal)) {
        await backendRemoveCredit(wallet);
        await setCartDiscounts(coupon ? [coupon] : []);
        throw new Error(coupon ? 'This coupon cannot be combined with DILI Credits. Remove the coupon or use credits separately.' : 'Shopify did not apply the DILI Credit discount.');
      }
      storage.set('dili-credit-code', data.code);
      storage.set('dili-credit-amount', data.amount);
      window.location.reload();
    } catch (e) {
      if (status) status.textContent = e.message || 'Could not apply DILI Credits.';
      if (button) button.disabled = false;
    }
  }

  document.addEventListener('click', (event) => {
    const wallet = event.target.closest('[data-dili-wallet]') || document.querySelector('[data-dili-wallet]');
    const retry = event.target.closest('[data-dili-wallet-retry]');
    if (retry) { event.preventDefault(); loadWallet(retry.closest('[data-dili-wallet]')); return; }
    const max = event.target.closest('[data-dili-credit-max]');
    if (max && wallet) {
      event.preventDefault();
      const input = wallet.querySelector('[data-dili-credit-input]');
      if (input) input.value = input.max || '0';
      return;
    }
    const apply = event.target.closest('[data-dili-credit-apply]');
    if (apply && wallet) { event.preventDefault(); applyCredits(wallet); return; }
    const remove = event.target.closest('[data-dili-credit-remove]');
    if (remove && wallet) { event.preventDefault(); removeCredits(wallet); return; }

    const preset = event.target.closest('[data-dili-discount-code]');
    if (preset) { event.preventDefault(); applyCoupon(preset.dataset.diliDiscountCode); return; }
    if (event.target.closest('[data-dili-safe-code-apply]')) {
      event.preventDefault(); applyCoupon(document.querySelector('[data-dili-safe-code-input]')?.value); return;
    }
  });

  const couponInput = document.querySelector('[data-dili-safe-code-input]');
  if (couponInput) couponInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(couponInput.value); } });
  document.querySelectorAll('[data-dili-wallet]').forEach(loadWallet);

  // Product recommendations remain isolated from the native Dawn cart.
  const section = document.querySelector('[data-dili-safe-recs]');
  if (!section) return;
  const grid = section.querySelector('[data-dili-safe-recs-grid]');
  const reshuffle = section.querySelector('[data-dili-safe-reshuffle]');
  const seed = section.dataset.productId;
  const excluded = new Set(String(section.dataset.excluded || '').split(',').map(s => s.trim()).filter(Boolean));
  let pool = [];
  const shuffle = (list) => { const out=list.slice(); for(let i=out.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[out[i],out[j]]=[out[j],out[i]];} return out; };
  const money = (cents) => moneyINR((Number(cents)||0)/100);
  function renderRecs() {
    if (!grid || !pool.length) return;
    grid.innerHTML = shuffle(pool).slice(0,6).map((p) => {
      const image = p.featured_image || (Array.isArray(p.images) ? p.images[0] : '');
      const url = p.url || `${root}products/${p.handle}`;
      return `<a class="dili-safe-rec" href="${esc(url)}"><div class="dili-safe-rec__image">${image ? `<img src="${esc(image)}" alt="${esc(p.title)}" loading="lazy">` : ''}</div><strong>${esc(p.title)}</strong><span>${money(p.price)}</span><small>VIEW PRODUCT →</small></a>`;
    }).join('');
    section.hidden = false;
  }
  async function loadRecs() {
    if (!seed) return;
    try {
      const intents=['complementary','related'];
      const responses=await Promise.all(intents.map(async(intent)=>{const r=await fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(seed)}&limit=10&intent=${intent}`,{headers:{Accept:'application/json'}});if(!r.ok)return[];const d=await r.json();return Array.isArray(d.products)?d.products:[];}));
      const seen=new Set();
      pool=responses.flat().filter((p)=>{if(!p||p.available===false||excluded.has(p.handle)||seen.has(p.handle))return false;seen.add(p.handle);return true;});
      renderRecs();
    } catch (_) {}
  }
  if (reshuffle) reshuffle.addEventListener('click', renderRecs);
  if ('requestIdleCallback' in window) requestIdleCallback(loadRecs,{timeout:1000}); else setTimeout(loadRecs,250);
})();
